import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import { useTween } from "@/hooks/useTween";
import type {
  BotConnectInterestOption,
  BotConnectInterestSchema,
  MaxConnectSchema,
  TelegramConnectSchema,
} from "@/schemas/sections";
import { readStorage, writeStorage } from "@/utils/safe-storage";
import { trackBotConnectInterestView } from "@/utils/tracking";
import {
  ConnectQrCodes,
  type ConnectQrTarget,
  useDesktopViewport,
  useQrCodes,
} from "./ConnectQrCodes";
import { CheckBadge, TelegramIcon } from "./icons";

export const TG_POLL_MS = 3000;
export const TG_POLL_FAST_MS = 1500;
// 10 minutes while a tap is unconfirmed — the OAuth device-flow shape ("leave the
// tab, confirm elsewhere, come back"), which is what this flow actually is. The
// old 3-minute cap gave up while a slow GC webhook was still in flight.
export const TG_POLL_MAX_MS = 600000;

/** Telegram Web, for the desktop visitor with no Telegram client installed —
 *  otherwise the deep link lands them on a dead protocol handler. */
export const TG_WEB_FALLBACK = "https://web.telegram.org";

/** Read the correlation token from the redirect (`?t=`). It is minted on the
 *  opt_in at submit and echoed by the GC bot funnel to `/api/tg-connect`, so the
 *  page can poll `/api/tg-connect/status` for the REAL connection confirmation. */
export function readConnectToken(): string {
  if (typeof window === "undefined") return "";
  try {
    return new URLSearchParams(window.location.search).get("t") ?? "";
  } catch {
    return "";
  }
}

async function saveBotConnectClick(provider: "telegram" | BotConnectInterestOption["provider"]) {
  const token = readConnectToken().trim();
  if (!token) return false;
  try {
    const response = await fetch("/api/tg-connect/click", {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({ token, provider }),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

export interface ConnectGate {
  /** The connect is CONFIRMED by the backend (or restored from this tab). */
  connected: boolean;
  /** The user tapped connect; we are waiting on the confirmation. */
  waiting: boolean;
  /** Whether the rest of the page (onboarding, cards) may render. True when the
   *  gate is inactive, the connect is confirmed, the user tapped "skip", or they
   *  opened MAX (which we cannot yet confirm — see `completed`). */
  revealed: boolean;
  /** Whether the step may be PRESENTED as done — the progress bar's 100% and the
   *  «Готово» eyebrow. Strictly narrower than `revealed`: opening MAX unlocks the
   *  page but proves nothing, so it must not render a completion claim over an
   *  un-connected block. Splitting these is what keeps the bar honest. */
  completed: boolean;
  /** True once the escape-hatch link should be offered. */
  showSkip: boolean;
  skip: () => void;
  markTapped: () => void;
  /** The visitor opened the MAX bot: reveal the rest of the page so they are not
   *  trapped behind a gate nothing can currently close, without claiming done. */
  markMaxTapped: () => void;
  /** The visitor says they scanned a QR. A camera fires NO click on this page, so
   *  without this a desktop visitor could connect on their phone and still be held
   *  behind the gate forever — `showSkip` only ever arms after a tap. Same
   *  non-committal reveal as `markMaxTapped`: it opens the page, it never claims
   *  the connect happened. */
  markQrScanned: () => void;
  /** Deep link, personal native-bind hash applied when it has arrived. */
  href: string;
  /** True once the backend reports a MAX connect. */
  maxConnected: boolean;
  /** `href`, but ONLY once it carries the personal bind hash — empty while it is
   *  still the bare-bot fallback. A QR must never encode the bare bot: scanning it
   *  would connect the visitor to a NEW GC user instead of their own. */
  personalTelegramHref: string;
  /** The visitor's PERSONAL MAX bind link, server-derived from the same native
   *  hash. Empty until it arrives, and empty forever for a visitor who has no
   *  hash (no `?t=` token, extraction skipped/failed, or already connected).
   *  Empty MUST mean "render no MAX action" — there is no safe generic fallback,
   *  see `MaxConnectSchema`. */
  maxHref: string;
  restored: boolean;
}

/** Owns the connect state for the whole section, because it GATES the rest of
 *  the page — the connect block alone can't hold it. Polls
 *  `/api/tg-connect/status` for the REAL confirmation (never optimistic on tap).
 *
 *  Gate semantics: the page starts CLOSED whenever an enabled connect block is
 *  configured, so the server-rendered HTML is already the single-CTA page and
 *  nothing flashes into view and back out. It opens when the backend confirms,
 *  when the user says they already connected, or when there is no `?t=` token to
 *  verify against (a direct visit — never leave that visitor at a dead end). */
export function useConnectGate(
  cfg: z.input<typeof TelegramConnectSchema> | undefined,
): ConnectGate {
  const active = !!cfg && cfg.enabled !== false;
  const [token, setToken] = useState("");
  const [connected, setConnected] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [skipped, setSkipped] = useState(false);
  // Set by an action we cannot verify (opening MAX, or reporting a QR scan). It
  // reveals the page WITHOUT feeding `completed` — see the return below.
  const [intentRevealed, setIntentRevealed] = useState(false);
  const [maxConnected, setMaxConnected] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  // No token means no way to verify — open the gate rather than trap the visitor.
  const [unverifiable, setUnverifiable] = useState(false);
  // A confirmed connect in this tab → show 100% straight away on return.
  const [restored, setRestored] = useState(false);
  // The user's personal native-bind hash (extracted async on submit; ~1-2s).
  const [nativeHash, setNativeHash] = useState("");
  // The server-composed personal MAX bind link. Derived from the SAME credential
  // as `nativeHash`, so it arrives and disappears with it.
  const [maxHref, setMaxHref] = useState("");
  const nativeHashRef = useRef("");
  nativeHashRef.current = nativeHash;

  useEffect(() => {
    if (!active) return;
    const tok = readConnectToken();
    setToken(tok);
    if (!tok) {
      setUnverifiable(true);
      return;
    }
    if (readStorage("session", `lc_tg_ok:${tok}`) === "1") {
      setConnected(true);
      setRestored(true);
    }
  }, [active]);

  // Offer the escape hatch only after the user has actually tapped connect and
  // the confirmation has had time to land — so it never competes with the CTA.
  useEffect(() => {
    if (!waiting || connected) return;
    const delay = Math.max(0, cfg?.skipAfterSeconds ?? 25) * 1000;
    const timer = window.setTimeout(() => setShowSkip(true), delay);
    return () => window.clearTimeout(timer);
  }, [waiting, connected, cfg?.skipAfterSeconds]);

  // One poll drives both: the personal native-bind hash for the deep link, and —
  // after the user taps — the GC-side connect confirmation that advances the bar
  // to 100%. Runs from mount to fetch the hash; keeps going while confirming a
  // tap. Re-polls on tab refocus (return from the Telegram app).
  useEffect(() => {
    if (!token || connected) return;
    let stop = false;
    const startedAt = Date.now();
    const poll = async () => {
      if (stop) return;
      try {
        const res = await fetch(`/api/tg-connect/status?t=${encodeURIComponent(token)}`, {
          headers: { accept: "application/json" },
        });
        if (res.ok) {
          const data = (await res.json()) as {
            connected?: boolean;
            maxConnected?: boolean;
            nativeHash?: string | null;
            maxUrl?: string | null;
          };
          // Mirror `/status`'s withholding: it drops the credential once EITHER
          // provider is connected, and the client must not keep a scannable
          // re-bind on screen after that.
          //
          // ⚠️ Clear only on that AUTHORITATIVE signal, never on a merely absent
          // field. `/status` fails OPEN — a DB hiccup answers 200 with the empty
          // shape so the client retries — so an unconditional clear would wipe a
          // perfectly good credential mid-session, dropping the visitor back to
          // the anonymous bare-bot link until the next poll.
          if (data?.connected || data?.maxConnected) {
            setNativeHash("");
            setMaxHref("");
          } else {
            if (data?.nativeHash) setNativeHash(data.nativeHash);
            if (data?.maxUrl) setMaxHref(data.maxUrl);
          }
          if (data?.maxConnected) setMaxConnected(true);
          if (data?.connected) {
            setConnected(true);
            writeStorage("session", `lc_tg_ok:${token}`, "1");
            return;
          }
        }
      } catch {}
      // Keep polling while confirming a tap, or until the native hash arrives.
      const needMore =
        !stop && Date.now() - startedAt < TG_POLL_MAX_MS && (waiting || !nativeHashRef.current);
      if (needMore) window.setTimeout(poll, waiting ? TG_POLL_MS : TG_POLL_FAST_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    poll();
    return () => {
      stop = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token, connected, waiting]);

  const base = cfg?.href ?? "https://t.me/lenacareer_bot";
  // Prefer the personal native-bind hash (binds Telegram to the EXACT GC user);
  // fall back to the bare bot (still connects, just not natively bound). The hash
  // is RAW on purpose — encoding/markdown must not touch the `__sN`.
  const href = nativeHash ? `${base}${base.includes("?") ? "&" : "?"}start=${nativeHash}` : base;

  // `completed` deliberately EXCLUDES `intentRevealed`: until a GC-side writer
  // marks `maxConnectedAt`, opening MAX (or scanning a code) tells us nothing, so
  // it may unlock the page but must not advance the bar or flip the eyebrow.
  const completed = !active || connected || skipped || unverifiable;
  return {
    connected,
    waiting,
    revealed: completed || intentRevealed,
    completed,
    showSkip: showSkip && !connected,
    skip: () => setSkipped(true),
    markTapped: () => setWaiting(true),
    markMaxTapped: () => setIntentRevealed(true),
    markQrScanned: () => {
      setIntentRevealed(true);
      // 🛑 Also arms the poll. `needMore` above stops polling once the hash has
      // arrived unless `waiting` is set, so without this a visitor who scanned
      // and completed the bind on their phone would NEVER be auto-confirmed —
      // the tab stops asking ~1.5 s after load and a desktop scanner never
      // leaves the tab, so even the visibilitychange re-poll does not fire.
      setWaiting(true);
    },
    href,
    maxConnected,
    personalTelegramHref: nativeHash ? href : "",
    maxHref,
    restored,
  };
}

/**
 * Which providers get a QR, if any.
 *
 * Desktop visitors cannot act on any button here — MAX's own web client logs in by
 * QR only — so both codes are offered BEFORE a provider is chosen.
 *
 * 🛑 Nothing is offered once connected, for the same reason `/status` stops serving
 * the hash (ADR-0105/0223): the code IS the bind credential, and a connected
 * account must never be handed a scannable re-bind. The check spans BOTH providers.
 */
export function resolveQrTargets({
  gate,
  isDesktop,
  showMax,
}: {
  gate: ConnectGate;
  isDesktop: boolean;
  showMax: boolean;
}): ConnectQrTarget[] {
  if (!isDesktop || gate.connected || gate.maxConnected) return [];
  const targets: ConnectQrTarget[] = [];
  // Telegram only gets a code when the link is the PERSONAL one — `href` falls
  // back to the bare bot, and a QR to that would bind a brand-new GC user.
  if (gate.personalTelegramHref) {
    targets.push({ provider: "telegram", href: gate.personalTelegramHref, label: "Telegram" });
  }
  if (showMax && gate.maxHref) {
    targets.push({ provider: "max", href: gate.maxHref, label: "MAX" });
  }
  return targets;
}

/** The prominent connect module (the dreamjob-weekend «остался последний шаг /
 *  50%» pattern) — presentational; `useConnectGate` owns the state. */
export function TelegramConnectBlock({
  cfg,
  gate,
  maxConnect,
  onMaxTapped,
  botConnectInterest,
  onInterestSelected,
  compact = false,
}: {
  cfg: z.input<typeof TelegramConnectSchema>;
  gate: ConnectGate;
  maxConnect?: z.input<typeof MaxConnectSchema>;
  onMaxTapped: () => void;
  botConnectInterest?: z.input<typeof BotConnectInterestSchema>;
  onInterestSelected: (option: BotConnectInterestOption) => void;
  compact?: boolean;
}) {
  const { connected, waiting, completed, restored, href } = gate;
  // MAX renders ONLY with a server-derived personal bind link. No link → no
  // button: the generic short link would connect the registrant as a brand-new
  // GC user (see `MaxConnectSchema`). This also inherits ADR-0164's
  // identified-visitor rule for free, because `maxHref` can only arrive from a
  // `?t=`-keyed status call.
  const showMax = !!maxConnect && maxConnect.enabled !== false && !!gate.maxHref && !connected;

  const isDesktop = useDesktopViewport();
  const qrTargets = resolveQrTargets({ gate, isDesktop, showMax });
  const qrCodes = useQrCodes(qrTargets);
  const start = cfg.progressStart ?? 50;
  const end = cfg.progressEnd ?? 100;
  // The bar tracks COMPLETION, not mere reveal: whenever the step is genuinely
  // finished — confirmed, skipped, or unverifiable — a 50% bar above unlocked
  // content would read as a broken promise. But an unconfirmable MAX tap only
  // reveals; claiming 100% there would be the opposite lie.
  const target = completed ? end : start;
  const progress = useTween(target, restored ? 0 : 900);

  // `completed` (not `connected`) drives the eyebrow for the same reason as the
  // bar. The check badge and «Готово! Проверьте Telegram» below stay tied to
  // `connected` — those assert a fact about Telegram we may not actually have.
  const eyebrow = completed
    ? "Готово"
    : waiting
      ? "Ожидаем подключение…"
      : (cfg.eyebrow ?? "Остался последний шаг");

  return (
    <div
      data-component="TelegramConnectBlock"
      className={clsx(
        "mx-auto max-w-xl rounded-3xl bg-brand text-brand-content shadow-lg md:mt-8 md:p-8",
        compact ? "mt-4 p-4" : "mt-6 p-6",
      )}
    >
      <Flex align="center" justify="between" gap={8} className="mb-2">
        <Text variant="label" as="p" className="text-brand-content/80">
          {eyebrow}
        </Text>
        <span className="text-(length:--type-heading) font-(weight:--fw-bold) tabular-nums">
          {Math.round(progress)}%
        </span>
      </Flex>
      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-brand-content/20"
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Прогресс регистрации"
      >
        <div className="h-full rounded-full bg-brand-content" style={{ width: `${progress}%` }} />
      </div>

      {connected ? (
        <Flex align="start" gap={8} className="mt-4">
          <CheckBadge />
          <div className="min-w-0 flex-1">
            <Text variant="subheading" as="p" className="text-brand-content">
              {cfg.connectedTitle ?? "Готово! Проверьте Telegram"}
            </Text>
            {cfg.connectedDescription && (
              <Text variant="body-sm" as="p" className="mt-1 text-brand-content/80">
                {cfg.connectedDescription}
              </Text>
            )}
          </div>
        </Flex>
      ) : (
        <div className="mt-4">
          <Text variant="subheading" as="h2" className="text-brand-content">
            {cfg.title}
          </Text>
          <Text variant="body-sm" as="p" className="mt-1 text-brand-content/85">
            {cfg.description}
          </Text>
          {waiting && (
            <Text variant="body-sm" as="p" className="mt-2 text-brand-content/70">
              Открыли Telegram? Нажмите «Старт» — как подключитесь, отметим здесь автоматически.
            </Text>
          )}
        </div>
      )}

      <ConnectQrCodes codes={qrCodes} onScanned={gate.markQrScanned} />

      <Stack gap={8} className="mt-5">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            gate.markTapped();
            void saveBotConnectClick("telegram");
          }}
          data-track="lm_next.cta.telegram_connect"
          className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-brand-content px-6 py-3.5 font-(weight:--fw-bold) text-brand no-underline shadow-md transition-transform duration-300 hover:-translate-y-0.5"
        >
          <TelegramIcon />
          {connected ? "Открыть Telegram" : (cfg.cta ?? "Подключить бота")}
        </a>
        {showMax && (
          <a
            href={gate.maxHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              void saveBotConnectClick("max");
              onMaxTapped();
            }}
            data-track="lm_next.cta.max_connect"
            className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-brand-content px-6 py-3.5 font-(weight:--fw-bold) text-brand no-underline shadow-md transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span
              aria-hidden="true"
              className="rounded-lg bg-brand/10 px-1.5 py-1 text-(length:--type-caption) font-(weight:--fw-heavy)"
            >
              MAX
            </span>
            {maxConnect?.label ?? "Подключить в MAX"}
          </a>
        )}
        <BotConnectInterestOptions cfg={botConnectInterest} onSelected={onInterestSelected} />
      </Stack>
      {cfg.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-brand-content/70">
          {cfg.note}
        </Text>
      )}
      {showMax && maxConnect?.note && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-brand-content/70">
          {maxConnect.note}
        </Text>
      )}
      {!connected && qrCodes.length === 0 && (
        <Text variant="body-sm" as="p" className="mt-3 text-center text-brand-content/70">
          Нет Telegram на этом устройстве?{" "}
          <a
            href={TG_WEB_FALLBACK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={gate.markTapped}
            data-track="lm_next.link.telegram_web"
            className="font-(weight:--fw-medium) text-brand-content underline"
          >
            Откройте его в браузере
          </a>
        </Text>
      )}
      {gate.showSkip && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={gate.skip}
            data-track="lm_next.cta.connect_skip"
            className="text-(length:--type-body-sm) font-(weight:--fw-medium) text-brand-content/70 underline"
          >
            {cfg.skipLabel ?? "Я подключил(а) — показать остальное"}
          </button>
        </div>
      )}
    </div>
  );
}

export function BotConnectInterestOptions({
  cfg,
  onSelected,
}: {
  cfg?: z.input<typeof BotConnectInterestSchema>;
  onSelected: (option: BotConnectInterestOption) => void;
}) {
  const [savingProvider, setSavingProvider] = useState<BotConnectInterestOption["provider"] | null>(
    null,
  );
  const [saveError, setSaveError] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewTracked = useRef(false);
  const options = cfg?.options ?? [];
  const hasOptions = options.length > 0;

  useEffect(() => {
    if (!hasOptions) return;
    const element = rootRef.current;
    if (!element || viewTracked.current) return;
    const markViewed = () => {
      if (viewTracked.current) return;
      viewTracked.current = true;
      trackBotConnectInterestView();
    };
    if (typeof IntersectionObserver === "undefined") {
      markViewed();
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5) {
          markViewed();
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasOptions]);

  if (!hasOptions) return null;

  const saveInterest = async (option: BotConnectInterestOption) => {
    setSavingProvider(option.provider);
    setSaveError(false);
    try {
      const saved = await saveBotConnectClick(option.provider);
      if (!saved) throw new Error("connect click was not saved");
      onSelected(option);
    } catch {
      setSaveError(true);
    } finally {
      setSavingProvider(null);
    }
  };

  return (
    <div ref={rootRef} data-component="BotConnectInterestOptions" className="grid gap-2">
      {options.map((option) => (
        <Flex
          as="button"
          key={option.provider}
          type="button"
          align="center"
          justify="center"
          gap={8}
          disabled={savingProvider !== null}
          aria-busy={savingProvider === option.provider}
          onClick={() => void saveInterest(option)}
          data-track={`lm_next.cta.${option.provider}_connect_interest`}
          className="min-h-12 w-full cursor-pointer rounded-2xl border-0 bg-brand-content px-6 py-3.5 text-(length:--type-body) font-(weight:--fw-bold) text-brand shadow-md transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-70"
        >
          <span
            aria-hidden="true"
            className="rounded-lg bg-brand/10 px-1.5 py-1 text-(length:--type-caption) font-(weight:--fw-heavy)"
          >
            {option.provider === "vk" ? "VK" : "MAX"}
          </span>
          <span>{savingProvider === option.provider ? "Сохраняем выбор…" : option.label}</span>
        </Flex>
      ))}
      {saveError && (
        <div role="alert" className="rounded-2xl bg-brand-content/10 p-3 text-center">
          <Text variant="body-sm" as="p" className="text-brand-content">
            {cfg?.saveError ?? "Не получилось сохранить выбор. Попробуйте ещё раз."}
          </Text>
        </div>
      )}
    </div>
  );
}

BotConnectInterestOptions.displayName = "BotConnectInterestOptions";

export function BotConnectInterestConfirmation({ option }: { option: BotConnectInterestOption }) {
  return (
    <Flex
      role="status"
      aria-live="polite"
      align="start"
      gap={12}
      data-component="BotConnectInterestConfirmation"
      className="mx-auto mt-6 max-w-xl rounded-3xl border border-brand/20 bg-surface-brand p-6 text-left md:mt-8"
    >
      <Flex
        as="span"
        aria-hidden="true"
        align="center"
        justify="center"
        className="h-9 w-9 shrink-0 rounded-full bg-brand/10 font-(weight:--fw-heavy) text-brand"
      >
        ✓
      </Flex>
      <div className="min-w-0 flex-1">
        <Text variant="label" as="p" className="text-fg">
          {option.comingSoonTitle}
        </Text>
        <Text variant="body-sm" as="p" className="mt-1 text-muted">
          {option.comingSoonDescription}
        </Text>
      </div>
    </Flex>
  );
}

BotConnectInterestConfirmation.displayName = "BotConnectInterestConfirmation";

export function isBotConnectInterestEnabled(
  connectEnabled: boolean,
  connectTokenAvailable: boolean,
  cfg: z.input<typeof BotConnectInterestSchema> | undefined,
): boolean {
  return (
    connectEnabled &&
    connectTokenAvailable &&
    !!cfg &&
    cfg.enabled !== false &&
    (cfg.options?.length ?? 0) > 0
  );
}

/** One upsell card — a full-tap link. Horizontal compact row on mobile, vibrant
 *  vertical card on desktop. Tone falls back to the default order by position. */
